import React, { useState, useRef, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { RouteComponentProps } from '../../models/RouteComponent';
import { getChecklistTemplates, getChecklistTemplate, updateChecklistTemplate } from '../../service/checklistService';
import { assignTemplateToUser } from '../../service/enhancedChecklistService';
import ChecklistTemplateCreateDialog from './ChecklistTemplateCreateDialog';

interface ClientChecklistTemplateTableProps {
  props: RouteComponentProps;
}

export default function ClientChecklistTemplateTable({ props }: ClientChecklistTemplateTableProps) {
  const toast = useRef<Toast>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
    appointment_name: { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
    trigger_type: { value: null as string | null, matchMode: FilterMatchMode.EQUALS },
    active: { value: null as boolean | null, matchMode: FilterMatchMode.EQUALS }
  });

  const triggerOptions = [
    // { label: 'DSTI', value: 'dsti' }, // Hidden while feature is being developed
    { label: 'Frequency', value: 'frequency' }
  ];

  const activeOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  const loadTemplates = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getChecklistTemplates();
      
      // Filter templates based on client's industry and conditions
      const clientTemplates = response.content?.filter((template: any) => {
        // Filter based on client's industry, employee count, tasks, equipment, vehicles, chemicals
        return isApplicableToClient(template);
      }) || [];
      
      setTemplates(clientTemplates);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load checklist templates'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates, props.session?.client_id]);

  // Check if template is applicable to current client
  const isApplicableToClient = (template: any) => {
    // This would contain business logic to filter templates based on:
    // - Client's industry
    // - Number of employees
    // - Types of tasks performed
    // - Equipment owned
    // - Vehicles owned
    // - Chemicals used
    // For now, return true for all templates
    return true;
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value || null;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    initFilters();
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      appointment_name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
      trigger_type: { value: null, matchMode: FilterMatchMode.EQUALS },
      active: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    setGlobalFilterValue('');
  };

  // Template Icon Body Template - READ ONLY for client
  const iconBodyTemplate = (rowData: any) => {
    return (
      <div className="flex align-items-center justify-content-center">
        {rowData.icon_url ? (
          <img 
            src={rowData.icon_url} 
            alt={rowData.name}
            style={{ width: '32px', height: '32px', borderRadius: '4px' }}
            onError={(e) => {
              // Fallback to default icon
              (e.target as HTMLImageElement).src = '/assets/default-checklist-icon.png';
            }}
          />
        ) : (
          <i className="pi pi-list" style={{ fontSize: '24px', color: '#6366f1' }} />
        )}
      </div>
    );
  };

  // Template Name Body Template - READ ONLY for client
  const nameBodyTemplate = (rowData: any) => {
    return (
      <div className="flex flex-column">
        <span className="font-semibold">{rowData.name}</span>
        {rowData.description && (
          <small className="text-color-secondary">{rowData.description}</small>
        )}
      </div>
    );
  };

  // Appointment Body Template - READ ONLY for client
  const appointmentBodyTemplate = (rowData: any) => {
    return (
      <span>{rowData.appointment_name || 'Not specified'}</span>
    );
  };

  // Trigger Body Template - Shows current trigger (DSTI or frequency)
  const triggerBodyTemplate = (rowData: any) => {
    if (rowData.trigger_type === 'dsti') {
      return (
        <div className="flex align-items-center gap-2">
          <span>DSTI</span>
          {canEditTemplate(rowData) && (
            <i className="pi pi-pencil text-color-secondary" style={{ fontSize: '12px' }} />
          )}
        </div>
      );
    } else {
      const frequency = rowData.frequency_value || 1;
      const type = rowData.frequency_type || 'days';
      return (
        <div className="flex align-items-center gap-2">
          <span>{frequency} {type}</span>
          {canEditTemplate(rowData) && (
            <i className="pi pi-pencil text-color-secondary" style={{ fontSize: '12px' }} />
          )}
        </div>
      );
    }
  };

  // Status Body Template with Toggle - Client can toggle active/inactive
  const statusBodyTemplate = (rowData: any) => {
    return (
      <InputSwitch 
        checked={rowData.active}
        onChange={(e) => toggleTemplateStatus(rowData, e.value)}
        disabled={!canEditTemplate(rowData)}
        tooltip={canEditTemplate(rowData) ? "Toggle active/inactive" : "Cannot modify this template"}
      />
    );
  };

  // Check if client can edit this template
  const canEditTemplate = (template: any) => {
    // Client can edit frequency, status, categories, questions, etc.
    // but not icon, name, appointment, or conditional requirements
    return template.client_editable !== false;
  };

  // Toggle template status
  const toggleTemplateStatus = async (template: any, newStatus: boolean) => {
    try {
      await updateChecklistTemplate(template.id, { active: newStatus });
      
      // Update local state
      setTemplates(prev => 
        prev.map((t: any) => 
          t.id === template.id ? { ...t, active: newStatus } : t
        )
      );

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Template ${newStatus ? 'activated' : 'deactivated'} successfully`
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update template status'
      });
    }
  };

  // View Template
  const viewTemplate = async (template: any) => {
    if (!template?.id) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    try {
      const fullTemplateData = await getChecklistTemplate(template.id);
      setSelectedTemplate(fullTemplateData.content);
      setEditDialogVisible(true);
    } catch (error) {
      console.error('Error fetching template details for viewing:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load template details'
      });
    }
  };

  // Edit Template
  const editTemplate = async (template: any) => {
    if (!canEditTemplate(template)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Access Restricted',
        detail: 'You can only view this template'
      });
      return;
    }

    if (!template?.id) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    try {
      const fullTemplateData = await getChecklistTemplate(template.id);
      setSelectedTemplate(fullTemplateData.content);
      setEditDialogVisible(true);
    } catch (error) {
      console.error('Error fetching template details:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load template details'
      });
    }
  };

  const testTemplate = async (template: any) => {
    if (!template?.id || !template?.name) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid template selected'
      });
      return;
    }

    // Check if user ID exists and is valid
    const userId = props.session?.sub;
    if (!userId || userId.trim() === '') {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'User session not found. Please log in again.'
      });
      return;
    }

    try {
      setLoading(true);
      
      await assignTemplateToUser(
        template.id,
        userId,
        props.session.client_id,
        props.session.site_id
      );
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Template "${template.name}" has been assigned to you for testing. Check your checklist instances in the mobile app.`,
        life: 5000
      });
    } catch (error) {
      console.error('Error assigning template for testing:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to assign template for testing'
      });
    } finally {
      setLoading(false);
    }
  };

  // Action Body Template
  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="flex gap-2 justify-content-center">
        <Button
          icon="pi pi-eye"
          rounded
          outlined
          onClick={() => viewTemplate(rowData)}
          tooltip="View Template"
          size="small"
        />
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          onClick={() => editTemplate(rowData)}
          tooltip={canEditTemplate(rowData) ? "Edit Template" : "View Only"}
          size="small"
          severity={canEditTemplate(rowData) ? "secondary" : "help"}
        />
        {rowData.active && (
          <Button
            icon="pi pi-play"
            rounded
            outlined
            onClick={() => testTemplate(rowData)}
            tooltip="Test Template"
            size="small"
            severity="success"
          />
        )}
      </div>
    );
  };

  // Filter templates
  const triggerFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={triggerOptions}
        onChange={(e) => options.filterCallback(e.value, options.index)}
        placeholder="Select trigger"
        className="p-column-filter"
        showClear
      />
    );
  };

  const activeFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={activeOptions}
        onChange={(e) => options.filterCallback(e.value, options.index)}
        placeholder="Select status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between align-items-center">
        <div className="flex align-items-center">
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilter}
            size="small"
          />
        </div>
        <div className="flex align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search templates..."
              size="small"
            />
          </span>
        </div>
      </div>
    );
  };

  const rightToolbarTemplate = () => {
    return (
      <div className="flex align-items-center gap-2">
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          outlined
          onClick={loadTemplates}
          size="small"
        />
      </div>
    );
  };

  const onDialogHide = () => {
    setEditDialogVisible(false);
    setSelectedTemplate(null);
  };

  const onTemplateSaved = () => {
    loadTemplates();
    onDialogHide();
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Template updated successfully'
    });
  };

  const header = renderHeader();

  return (
    <div>
      <Toast ref={toast} />
      
      <Card 
        title="Checklist Templates" 
        subTitle="Templates applicable to your industry and business conditions"
        className="mb-4"
      >
        <div className="mb-3 p-3 bg-blue-50 border-round">
          <div className="flex align-items-center gap-2 mb-2">
            <i className="pi pi-info-circle text-blue-600"></i>
            <span className="font-semibold text-blue-800">Client Admin Portal</span>
          </div>
          <p className="text-sm text-blue-700 m-0">
            These templates are created by DSF Root Admin and filtered based on your industry and business conditions. 
            You can edit frequency, categories, questions, and corrective actions, but cannot modify template icons, 
            names, appointments, or conditional requirements.
          </p>
        </div>

        <Toolbar
          className="mb-4"
          right={rightToolbarTemplate}
        />

        <DataTable
          value={templates}
          loading={loading}
          dataKey="id"
          filters={filters}
          filterDisplay="menu"
          globalFilterFields={['name', 'appointment_name', 'trigger_type']}
          header={header}
          emptyMessage="No applicable checklist templates found for your organization."
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} templates"
          size="small"
          stripedRows
          removableSort
        >
          <Column
            header="Icon"
            body={iconBodyTemplate}
            style={{ width: '80px', textAlign: 'center' }}
          />
          
          <Column
            field="name"
            header="Template Name"
            body={nameBodyTemplate}
            sortable
            filter
            filterPlaceholder="Search by name"
            style={{ minWidth: '250px' }}
          />
          
          <Column
            field="appointment_name"
            header="Appointment"
            body={appointmentBodyTemplate}
            sortable
            filter
            filterPlaceholder="Search by appointment"
            style={{ minWidth: '150px' }}
          />
          
          <Column
            header="Trigger"
            body={triggerBodyTemplate}
            sortable
            filter
            filterElement={triggerFilterTemplate}
            style={{ minWidth: '120px' }}
          />
          
          <Column
            field="active"
            header="Status"
            body={statusBodyTemplate}
            sortable
            filter
            filterElement={activeFilterTemplate}
            style={{ minWidth: '100px', textAlign: 'center' }}
          />
          
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ minWidth: '120px', textAlign: 'center' }}
            header="Actions"
          />
        </DataTable>
      </Card>

      <Dialog 
        header={selectedTemplate?.name ? `Edit Template: ${selectedTemplate.name}` : 'View Template'} 
        visible={editDialogVisible} 
        style={{ width: '90vw', maxWidth: '1200px' }} 
        onHide={onDialogHide}
        draggable={false}
        resizable={false}
        maximizable
      >
        <ChecklistTemplateCreateDialog
          visible={true}
          onHide={onDialogHide}
          template={selectedTemplate}
          onSave={onTemplateSaved}
          session={props.session}
        />
      </Dialog>
    </div>
  );
}
